<template>
  <div>
    <WdsAlert v-if="error" error icon class="tw-mb-4">
      {{ $t('Error loading compliance data') }}
      <template #description>{{ error.message }}</template>
    </WdsAlert>
    <div v-else-if="loading" class="tw-p-4">
      <WdsSpinner indeterminate :size="40" />
    </div>
    <template v-else-if="isApplicable">
      <WdsText secondary class="tw-mb-6">
        {{
          $t(
            'As a staffing agency operating in the Netherlands, you need to provide compliance information for regulatory requirements.',
          )
        }}
      </WdsText>

      <div v-if="waadiCompliance">
        <WdsHeading as="h3" type="title-small" class="tw-mb-2">
          {{ $t('WAADI Registration') }}
        </WdsHeading>
        <NlWaadiRegistrationSettingsForm
          :recruiter="activeAccount"
          :compliance-data="waadiCompliance.data"
        />
      </div>

      <WdsDivider v-if="waadiCompliance && snaCompliance" class="tw-my-6" />

      <div v-if="snaCompliance">
        <WdsHeading as="h3" type="title-small" class="tw-mb-2">
          {{ $t('SNA Quality Mark (NEN 4400-1)') }}
        </WdsHeading>
        <NlSnaCertificationSettingsForm
          :recruiter="activeAccount"
          :compliance-data="snaCompliance.data"
          @saved="onComplianceSaved"
        />
      </div>

      <WdsDivider v-if="snaCompliance && gAccountCompliance" class="tw-my-6" />

      <div v-if="gAccountCompliance">
        <WdsHeading as="h3" type="title-small" class="tw-mb-2">
          {{ $t('G-Account (Geblokkeerde Rekening)') }}
        </WdsHeading>
        <NlGAccountSettingsForm
          :recruiter="activeAccount"
          :compliance-data="gAccountCompliance.data"
          :sna-status="snaCompliance?.data?.snaStatus"
        />
      </div>

      <WdsDivider v-if="(gAccountCompliance || snaCompliance || waadiCompliance) && kvkCertificateCompliance" class="tw-my-6" />

      <div v-if="kvkCertificateCompliance">
        <WdsHeading as="h3" type="title-small" class="tw-mb-2">
          {{ $t('KvK documents') }}
        </WdsHeading>
        <NlKvkCertificateForm
          compliance-name="RECRUITER_NL_KVK_CERTIFICATE"
          :compliance-data="kvkCertificateCompliance.data"
          @saved="onComplianceSaved"
        />
      </div>
    </template>
    <WdsAlert v-else info icon>
      {{ $t('Netherlands compliance settings are not applicable to your account.') }}
    </WdsAlert>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { WdsAlert, WdsDivider, WdsHeading, WdsSpinner, WdsText } from '@worksome/wds-components'
import NlGAccountSettingsForm from '@/components/compliances/settings/NlGAccountSettingsForm.vue'
import NlKvkCertificateForm from '@/components/compliances/settings/NlKvkCertificateForm.vue'
import NlSnaCertificationSettingsForm from '@/components/compliances/settings/NlSnaCertificationSettingsForm.vue'
import NlWaadiRegistrationSettingsForm from '@/components/compliances/settings/NlWaadiRegistrationSettingsForm.vue'
import { ACTIVE_COMPLIANCES_ON_ACCOUNT_FRAGMENT } from '@/components/compliances/queries/Compliances'
import { ParseJSONPropertyInObject } from '@/utils/ParseJSONPropertyInObject'

const complianceNames = [
  'NL_SNA_CERTIFICATION',
  'NL_WAADI_REGISTRATION',
  'NL_G_ACCOUNT',
  'RECRUITER_NL_KVK_CERTIFICATE',
]

const { result, loading, error, refetch } = useQuery(
  gql`
    query recruiterNlCompliancesQuery($complianceNames: [ComplianceName!]!) {
      me {
        activeAccount {
          __typename
          ...ActiveCompliancesOnAccount
        }
      }
    }
    ${ACTIVE_COMPLIANCES_ON_ACCOUNT_FRAGMENT}
  `,
  {
    complianceNames,
  },
)

const onComplianceSaved = () => {
  refetch()
}

const activeAccount = computed(() => result?.value?.me?.activeAccount)

const compliances = computed(
  () =>
    activeAccount.value?.compliances
      ?.filter((compliance) => compliance.isApplicable)
      ?.map((compliance) => ParseJSONPropertyInObject('data', compliance)) || [],
)

const snaCompliance = computed(() =>
  compliances.value.find((c) => c.name === 'NL_SNA_CERTIFICATION'),
)

const gAccountCompliance = computed(() => compliances.value.find((c) => c.name === 'NL_G_ACCOUNT'))

const waadiCompliance = computed(() =>
  compliances.value.find((c) => c.name === 'NL_WAADI_REGISTRATION'),
)

const kvkCertificateCompliance = computed(() =>
  compliances.value.find((c) => c.name === 'RECRUITER_NL_KVK_CERTIFICATE'),
)

const isApplicable = computed(() => compliances.value.length > 0)
</script>
